/**
 * 商品画像。**無いことと、出せなかったことを言い分ける。**
 *
 * デモ（v2）の見せ方に合わせてある。一覧は小さな縦横比つきの札、詳細は
 * 区画いっぱいの 1 枚で、どちらも出せないときは決まった代わりの表示になる。
 *
 * ## 出せないときに空の枠を残さない
 *
 * 取り込めていない商品（`imageUrl` が null）と、URL はあるのに読み込めな
 * かった商品は別のことである。前者は「画像は取り込めていません」、後者は
 * 「画像を読み込めませんでした」と出す。どちらも**何も無い四角**にはしない
 * （空欄を置くと、画像が無いのか画面が壊れているのかが読み手に分からない）。
 *
 * ## 画像の取得元へ、こちらの事情を渡さない
 *
 * `referrerPolicy="no-referrer"` を付ける。商品画像は事業者側のドメインや
 * CDN に置かれていて、こちらの管理画面の URL（テナントの識別子が入りうる）
 * を Referer として送る理由が無い。
 *
 * 一覧は行数ぶん並ぶので `loading="lazy"`。画面に入るまで取りに行かない。
 *
 * ## 寸法を先に決める
 *
 * `width` / `height` を必ず持たせ、読み込みの前後で行の高さが動かないように
 * する。詳細は幅いっぱいなので、比率だけを CSS 側（`aspect-ratio`）で持つ。
 */

import { useState } from "react";

/** 一覧の札の寸法（デモの 64 × 48 に合わせる）。 */
const THUMB_WIDTH = 64;
const THUMB_HEIGHT = 48;

/** 取り込めていないときの文面。**「読み込めなかった」と混ぜない。** */
const MISSING_LABEL = "画像なし";
const MISSING_DESCRIPTION = "この商品の画像は取り込めていません。";

/** URL はあるのに出せなかったときの文面。 */
const BROKEN_LABEL = "画像を出せません";
const BROKEN_DESCRIPTION =
  "画像の URL はありますが、読み込めませんでした。取得先が消えている・許可されていない宛先であることがあります。";

function Placeholder({
  text,
  variant,
}: {
  text: string;
  variant: "thumb" | "full";
}) {
  return (
    <div
      className={`vd-product-image vd-product-image--${variant} vd-product-image--empty`}
      role="img"
      aria-label={text}
    >
      <span className="vd-stat-note">{text}</span>
    </div>
  );
}

/**
 * 一覧に並べる小さな商品画像。
 *
 * `name` は代替テキストに使う。名前が入っていない商品もあるので、その
 * ときは「商品画像」とだけ書く（`null` を文字列にして並べない）。
 */
export function ProductThumbnail({
  imageUrl,
  name,
}: {
  readonly imageUrl: string | null;
  readonly name: string | null;
}) {
  const [broken, setBroken] = useState(false);
  if (imageUrl == null || imageUrl === "") {
    return <Placeholder text={MISSING_LABEL} variant="thumb" />;
  }
  if (broken) {
    return <Placeholder text={BROKEN_LABEL} variant="thumb" />;
  }
  return (
    <img
      className="vd-product-image vd-product-image--thumb"
      src={imageUrl}
      alt={name == null || name === "" ? "商品画像" : `${name} の商品画像`}
      width={THUMB_WIDTH}
      height={THUMB_HEIGHT}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => {
        setBroken(true);
      }}
    />
  );
}

/**
 * 商品詳細の 1 枚。
 *
 * 出せないときは、一覧より詳しい文面にする（1 件だけを見ている画面なので、
 * 「なぜ出ていないのか」をその場で読めたほうがよい）。
 */
export function ProductImage({
  imageUrl,
  name,
}: {
  readonly imageUrl: string | null;
  readonly name: string | null;
}) {
  const [broken, setBroken] = useState(false);
  if (imageUrl == null || imageUrl === "") {
    return <Placeholder text={MISSING_DESCRIPTION} variant="full" />;
  }
  if (broken) {
    return <Placeholder text={BROKEN_DESCRIPTION} variant="full" />;
  }
  return (
    <img
      className="vd-product-image vd-product-image--full"
      src={imageUrl}
      alt={name == null || name === "" ? "商品画像" : `${name} の商品画像`}
      loading="lazy"
      referrerPolicy="no-referrer"
      onError={() => {
        setBroken(true);
      }}
    />
  );
}
